<template>
  <div class="blog-list-container">
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="5" animated />
    </div>

    <div v-else class="blog-list-content">
      <div class="header">
        <h2>{{ (isAdmin || isSuperAdmin) ? '文章管理' : '我的文章' }}</h2>
        <el-button type="primary" @click="createNewBlog">写新文章</el-button>
      </div>

      <el-empty v-if="blogs.length === 0" description="暂无文章" />

      <div v-else class="masonry-grid">
        <div v-for="blog in blogs" :key="blog.id" class="masonry-item">
          <el-card class="blog-card" shadow="hover" @click="viewBlog(blog.id)">
            <div class="card-header">
              <h3 class="blog-title">
                {{ blog.title }}
                <el-tag
                  v-if="blog.onlyAdmin === 1"
                  type="warning"
                  size="small"
                  class="only-admin-badge"
                >
                  Only Admin
                </el-tag>
              </h3>
            </div>
            <div class="card-content">
              <div v-if="blog.banner" class="banner-container">
                <img :src="blog.banner" class="blog-banner" alt="Blog banner" />
              </div>
              <p class="blog-summary">{{ blog.summary }}</p>
              <div class="blog-meta">
                <div class="tags">
                  <el-tag v-for="tag in blog.tags" :key="tag" size="small" class="tag">
                    {{ tag }}
                  </el-tag>
                </div>
                <div class="categories">
                  <el-tag v-for="category in blog.categories" :key="category" size="small" type="success" class="category">
                    {{ category }}
                  </el-tag>
                </div>
              </div>
            </div>
            <div class="card-actions">
              <div class="status-info">
                <span class="status-text" :class="{
                  'status-published': blog.isPublished,
                  'status-draft': !blog.isPublished
                }">
                  {{ blog.isPublished ? '已发布' : '草稿' }}
                </span>
                <el-tag
                  :type="blog.isApproved === 1 ? 'success' : 'warning'"
                  size="small"
                  class="audit-tag"
                >
                  {{ blog.isApproved === 1 ? '已审核' : '待审核' }}
                </el-tag>
              </div>
              <el-button-group>
                <el-button type="primary" size="small" @click.stop="editBlog(blog.id)">编辑</el-button>
                <el-button type="info" size="small" @click.stop="viewBlog(blog.id)">查看</el-button>
                <el-button type="success" size="small" @click.stop="publishBlog(blog.id)" v-if="!blog.isPublished">发布</el-button>
                <el-button type="warning" size="small" @click.stop="unpublishBlog(blog.id)" v-else>取消发布</el-button>
                <el-button type="danger" size="small" @click.stop="deleteBlog(blog.id)">删除</el-button>
                <el-button 
                  v-if="(isAdmin || isSuperAdmin) && blog.authorId !== currentUserId" 
                  type="warning" 
                  size="small" 
                  @click.stop="adminEditBlog(blog.id)"
                  class="admin-button">
                  管理员编辑
                </el-button>
              </el-button-group>
            </div>
          </el-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import { hasSysRole } from '@/utils/sysRole'
//import authManager from '@/utils/auth'

const router = useRouter()
const loading = ref(true)
const blogs = ref([])

// Role detection and state management
const isAdmin = ref(false)
const isSuperAdmin = ref(false)
const currentUserId = ref(null)

// Check user role and permissions
const checkUserRole = () => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')
    const sysRole = userInfo.sysRole
    
    isAdmin.value = hasSysRole(sysRole, 'admin')
    isSuperAdmin.value = hasSysRole(sysRole, 'superadmin')
    currentUserId.value = userInfo.uuid
    
    console.log('User role detection:', {
      sysRole,
      isAdmin: isAdmin.value,
      isSuperAdmin: isSuperAdmin.value,
      currentUserId: currentUserId.value
    })
  } catch (error) {
    console.error('Error checking user role:', error)
    // Default to non-admin if role detection fails
    isAdmin.value = false
    isSuperAdmin.value = false
    currentUserId.value = null
  }
}

const fetchMyBlogs = async () => {
  try {
    loading.value = true
    const token = localStorage.getItem('jwt_token')
    
    // For admin/superadmin users, fetch all blogs; for regular users, fetch only their own
    const endpoint = (isAdmin.value || isSuperAdmin.value) ? '/api/v1/blog/list' : '/api/v1/blog/list/my'
    
    const response = await axios.post(endpoint, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.data.code === 200) {
      blogs.value = response.data.data.blogs
    } else {
      ElMessage.error(response.data.message || '获取文章列表失败')
    }
  } catch (error) {
    console.error('Error fetching blogs:', error)
    ElMessage.error('获取文章列表失败')
  } finally {
    loading.value = false
  }
}

const createNewBlog = () => {
  router.push('/blogs/create')
}

const viewBlog = (id) => {
  router.push(`/blogs/${id}`)
}

const editBlog = (id) => {
  router.push(`/blogs/edit/${id}`)
}

const publishBlog = async (id) => {
  try {
    const token = localStorage.getItem('jwt_token')
    const response = await axios.post('/api/v1/blog/publish', { id }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.data.code === 200) {
      ElMessage.success(response.data.message || '发布成功')
      await fetchMyBlogs()
    } else {
      ElMessage.error(response.data.message || '发布失败')
    }
  } catch (error) {
    console.error('Error publishing blog:', error)
    ElMessage.error('发布失败')
  }
}

const unpublishBlog = async (id) => {
  try {
    const token = localStorage.getItem('jwt_token')
    const response = await axios.post('/api/v1/blog/unpublish', { id }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.data.code === 200) {
      ElMessage.success('已取消发布')
      await fetchMyBlogs()
    } else {
      ElMessage.error(response.data.message || '操作失败')
    }
  } catch (error) {
    console.error('Error unpublishing blog:', error)
    ElMessage.error('操作失败')
  }
}

const deleteBlog = async (id) => {
  try {
    await ElMessageBox.confirm('确定要删除这篇文章吗？', '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })

    const token = localStorage.getItem('jwt_token')
    const response = await axios.post('/api/v1/blog/delete', { id }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.data.code === 200) {
      ElMessage.success('删除成功')
      await fetchMyBlogs()
    } else {
      ElMessage.error(response.data.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Error deleting blog:', error)
      ElMessage.error('删除失败')
    }
  }
}

// Admin edit function for navigation
const adminEditBlog = (id) => {
  router.push(`/blogs/edit/${id}`)
}

onMounted(() => {
  checkUserRole()
  fetchMyBlogs()
})
</script>

<style scoped>
.blog-list-container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 20px;
}

.loading-container {
  padding: 20px;
}

.blog-list-content {
  background-color: #fff;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.header h2 {
  margin: 0;
  color: var(--el-text-color-primary);
}

.masonry-grid {
  column-count: 4;
  column-gap: 16px;
  padding: 0;
}

.masonry-item {
  break-inside: avoid;
  margin-bottom: 16px;
  display: block;
}

.blog-card {
  height: auto;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
}

.blog-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.card-header {
  padding-bottom: 15px;
  border-bottom: 1px solid var(--el-border-color-light);
}

.card-header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--el-text-color-primary);
}

.card-content {
  flex: 1;
  padding: 15px 0;
  display: flex;
  flex-direction: column;
}

.banner-container {
  width: 100%;
  margin-bottom: 15px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.blog-banner {
  width: 100%;
  height: 180px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.blog-banner:hover {
  transform: scale(1.02);
}

.blog-title {
  margin: 0;
  font-size: 18px;
  color: var(--el-text-color-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.only-admin-badge {
  flex-shrink: 0;
}

.blog-title:hover {
  color: var(--el-color-primary);
}

.blog-summary {
  font-size: 14px;
  color: var(--el-text-color-primary);
  margin-bottom: 10px;
  line-height: 1.4;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.blog-meta {
  margin-bottom: 10px;
}

.tags, .categories {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 5px;
}

.card-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid var(--el-border-color-light);
}

.status-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-text {
  font-weight: 500;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 4px;
}

.status-published {
  color: #67c23a;
  background-color: #f0f9ff;
  border: 1px solid #b3d8ff;
}

.status-draft {
  color: #e6a23c;
  background-color: #fefcf8;
  border: 1px solid #f5f0e6;
}

.admin-button {
  background-color: #e6a23c;
  border-color: #e6a23c;
  color: #fff;
}

.admin-button:hover {
  background-color: #d4941a;
  border-color: #d4941a;
}

@media (max-width: 1200px) {
  .masonry-grid {
    column-count: 3;
  }
}

@media (max-width: 900px) {
  .masonry-grid {
    column-count: 2;
  }
}

@media (max-width: 768px) {
  .blog-list-container {
    padding: 10px;
  }

  .blog-list-content {
    padding: 15px;
  }

  .masonry-grid {
    column-count: 2;
    column-gap: 12px;
  }

  .masonry-item {
    margin-bottom: 12px;
  }

  .blog-banner {
    height: 150px;
  }

  .blog-title {
    font-size: 1.1em;
  }

  .blog-summary {
    -webkit-line-clamp: 2;
  }

  .card-actions .el-button {
    font-size: 12px;
    padding: 4px 8px;
  }

  .header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .header h2 {
    font-size: 1.5em;
  }
}

@media (max-width: 480px) {
  .masonry-grid {
    column-count: 1;
    column-gap: 12px;
  }
}
</style> 